import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useChangeMyPassword } from "@/hooks/queries/auth/useProfile";
import { useToastStore } from "@/stores/useToastStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { detectHost } from "@/lib/host";
import { queryClient } from "@/lib/queryClient";
import { AuthShell } from "@/components/layout/AuthShell";
import { Button } from "@/components/common/Button";
import { FormField } from "@/components/common/FormField";
import { Input } from "@/components/common/Input";

// Backend policy floor (app/security/passwords.py validate_password: len < 12).
const PASSWORD_MIN = 12;

/**
 * WS-E — forced temp-password rotation screen (invariant #9 sibling).
 *
 * Reached two ways:
 *   1. The global ``apiClient`` response interceptor redirects here on a 403
 *      ``password_change_required`` from ANY authed endpoint (the real entry
 *      point for a flagged user who tries to use the app).
 *   2. Direct navigation (the route is registered on both hosts).
 *
 * A flagged user was onboarded with a one-time temporary password (SA-invite
 * or revoke). They must supply that temp password as ``current_password`` plus
 * a new password; the backend's ``change_password`` service clears
 * ``requires_password_reset`` and consumes the temp credential. POSTs to the
 * backend-allowlisted ``/users/me/change-password`` so this screen itself is
 * reachable while the gate is active (it never 403s back into the interceptor).
 */
export default function ForcedPasswordChange(): JSX.Element {
  const { tenantSlug } = useParams<{ tenantSlug: string }>();
  const navigate = useNavigate();
  const changePw = useChangeMyPassword();
  const addToast = useToastStore((state) => state.addToast);
  const storeSlug = useAuthStore((state) => state.tenantSlug);

  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [fieldError, setFieldError] = useState<string | null>(null);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFieldError(null);

    if (newPassword.length < PASSWORD_MIN) {
      setFieldError(`New password must be at least ${PASSWORD_MIN} characters.`);
      return;
    }
    if (newPassword !== confirmPassword) {
      setFieldError("Passwords do not match.");
      return;
    }

    changePw.mutate(
      { current_password: currentPassword, new_password: newPassword },
      {
        onSuccess: () => {
          // The backend cleared requires_password_reset; drop any cached /auth/me
          // so the app re-fetches a clean (unflagged) principal.
          queryClient.invalidateQueries({ queryKey: ["auth", "me"] });
          addToast("Password updated. Welcome in.", "success");
          // Route into the app. Admin host has no slug; tenant host uses the
          // slug from the route param, falling back to the JWT-derived slug.
          const dest =
            detectHost() === "admin"
              ? "/dashboard"
              : `/${tenantSlug ?? storeSlug ?? ""}/dashboard`;
          navigate(dest, { replace: true });
        },
        onError: (err) => {
          switch (err.error) {
            case "invalid_credentials":
              setFieldError("That temporary password is incorrect.");
              break;
            case "weak_password": {
              const reasons = Array.isArray(err.details["reasons"])
                ? (err.details["reasons"] as string[]).join(", ")
                : "Password is too weak.";
              setFieldError(reasons);
              break;
            }
            default:
              setFieldError(err.message || "That didn't go through. Try again.");
          }
        },
      },
    );
  };

  return (
    <AuthShell
      title="Set a new password"
      subtitle={`Your account uses a temporary password. Choose a new one — at least ${PASSWORD_MIN} characters.`}
    >
      <form onSubmit={handleSubmit} className="space-y-4">
        <FormField label="Temporary password" htmlFor="currentPassword">
          <Input
            id="currentPassword"
            type="password"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            required
          />
        </FormField>
        <FormField label="New password" htmlFor="newPassword">
          <Input
            id="newPassword"
            type="password"
            autoComplete="new-password"
            value={newPassword}
            onChange={(e) => setNewPassword(e.target.value)}
            required
            hasError={!!fieldError}
          />
        </FormField>
        <FormField
          label="Confirm new password"
          htmlFor="confirmPassword"
          error={fieldError ?? undefined}
        >
          <Input
            id="confirmPassword"
            type="password"
            autoComplete="new-password"
            value={confirmPassword}
            onChange={(e) => setConfirmPassword(e.target.value)}
            required
            hasError={!!fieldError}
          />
        </FormField>
        <Button type="submit" className="w-full" loading={changePw.isPending}>
          Set new password
        </Button>
      </form>
    </AuthShell>
  );
}
